/**
 * Labels and rules for a company's billing terms, shared by the Billing tab,
 * the Employees table and the case pane. The server enforces the same rules;
 * these keep the controls from offering what it would reject.
 */
import { formatMoney } from "../timeTracking/utils";

import type {
  ApprovedRate,
  CaseFee,
  CompanyBilling,
  ExportFormat,
  ExportGrouping,
  ExportSetup,
  FeeBasis,
  FeeScheduleRow,
  PoField
} from "./types";

export const LEGAL_TRACKER = "Legal Tracker";

export const GROUPING_OPTIONS: ReadonlyArray<{
  id: ExportGrouping;
  label: string;
}> = [
  { id: "matter", label: "One invoice per employee matter" },
  { id: "entity", label: "One invoice per entity" }
];

export const FORMAT_OPTIONS: ReadonlyArray<{
  id: ExportFormat;
  label: string;
}> = [
  { id: "ledes_1998b", label: "LEDES 1998B" },
  { id: "ledes_1998bi", label: "LEDES 1998BI" },
  { id: "csv", label: "CSV" }
];

export const PO_FIELD_OPTIONS: ReadonlyArray<{ id: PoField; label: string }> = [
  { id: "description", label: "Invoice description (LEDES 1998B)" },
  { id: "po_number", label: "PO_NUMBER (LEDES 1998BI)" }
];

export const BASIS_OPTIONS: ReadonlyArray<{ id: FeeBasis; label: string }> = [
  { id: "flat", label: "Flat fee" },
  { id: "hourly_capped", label: "Hourly, not to exceed" },
  { id: "hourly", label: "Hourly" }
];

export const SHARE_OPTIONS = [
  { id: "100", label: "100%" },
  { id: "50", label: "50%" },
  { id: "0", label: "0%" }
] as const;

export const formatLabel = (f: ExportFormat | null | undefined): string =>
  FORMAT_OPTIONS.find((o) => o.id === f)?.label ?? "Firm default";

export const feeText = (row: Pick<FeeScheduleRow, "basis" | "amount">) => {
  if (row.basis === "hourly") return "Hourly";
  const amount = formatMoney(row.amount ?? 0);
  return row.basis === "flat" ? `${amount} flat` : `Hourly, NTE ${amount}`;
};

/** Why a row can't change who pays, or null when the firm can split it. */
export const lockLabel = (
  row: Pick<FeeScheduleRow, "case_type">,
  billing: Pick<CompanyBilling, "locks" | "response_case_type">
): string | null => {
  if (row.case_type === billing.response_case_type)
    return "Follows the petition";
  return billing.locks[row.case_type] ? "Required by law" : null;
};

export const lockReason = (
  row: Pick<FeeScheduleRow, "case_type">,
  billing: Pick<CompanyBilling, "locks" | "response_case_type">
): string =>
  billing.locks[row.case_type] ??
  "RFE and NOID responses defend the employer's petition, so the employer pays them like the petition.";

/** Choosing a format moves the PO to the field that format has. */
export const withFormat = (
  setup: ExportSetup,
  format: ExportFormat
): ExportSetup => ({
  ...setup,
  format,
  po_field: format === "ledes_1998bi" ? "po_number" : "description"
});

/** PO_NUMBER only exists in 1998BI, so choosing it switches the format. */
export const withPoField = (
  setup: ExportSetup,
  poField: PoField
): ExportSetup => ({
  ...setup,
  po_field: poField,
  format:
    poField === "po_number"
      ? "ledes_1998bi"
      : setup.format === "ledes_1998bi"
      ? "ledes_1998b"
      : setup.format
});

export const groupingConflict = (setup: ExportSetup): boolean =>
  setup.ebilling_system === LEGAL_TRACKER && setup.grouping === "entity";

export type Tone = "primary" | "emerald" | "amber" | "red";

export const rateEffect = (
  rate: ApprovedRate | undefined,
  standard: number | null,
  formatDate: (iso: string) => string
): { tone: Tone; label: string } => {
  if (rate?.status === "approved" && rate.rate) {
    return standard && rate.rate < standard
      ? { tone: "primary", label: `Priced at ${formatMoney(rate.rate)}/h` }
      : { tone: "emerald", label: "Approved" };
  }
  if (rate?.status === "pending") {
    const when = rate.submitted_at ? ` ${formatDate(rate.submitted_at)}` : "";
    return {
      tone: "amber",
      label: `Submitted${when} · pending · export blocked`
    };
  }
  return { tone: "red", label: "No approved rate · lines rejected" };
};

/** Timekeepers whose lines this company's e-billing would reject. */
export const missingRates = (
  billing: Pick<CompanyBilling, "approved_rates" | "timekeepers">
): number => {
  if (!billing.approved_rates.length) return 0;
  const approved = new Set(
    billing.approved_rates
      .filter((r) => r.status === "approved" && r.rate)
      .map((r) => r.user_id)
  );
  return billing.timekeepers.filter((t) => !approved.has(t.user_id)).length;
};

/** "Approved rate" rows saved back with one timekeeper's row replaced. */
export const ratesWith = (
  rows: ApprovedRate[],
  userId: string,
  rate: number | null
): ApprovedRate[] => {
  const others = rows.filter((r) => r.user_id !== userId);
  const prior = rows.find((r) => r.user_id === userId);
  if (!rate) return prior?.status === "pending" ? rows : others;
  return [
    ...others,
    {
      user_id: userId,
      rate,
      status: "approved",
      effective_from: prior?.effective_from ?? null,
      effective_to: prior?.effective_to ?? null,
      submitted_at: null,
      submitted_by: null
    }
  ];
};

export const shareText = (share: number): string =>
  share >= 100 ? "–" : `${100 - share}% · exported under them`;

/** The fee and progress shown on a case: "$4,500 flat" or "$1,200 of $3,000". */
export const caseFeeText = (fee: CaseFee): string => {
  if (fee.arrangement === "flat_fee")
    return `${formatMoney(fee.amount ?? 0)} flat`;
  if (fee.arrangement === "hourly_capped" && fee.amount)
    return `${formatMoney(fee.spent)} of ${formatMoney(fee.amount)}`;
  if (fee.arrangement === "non_billable") return "Not billed";
  return `${formatMoney(fee.spent)} hourly`;
};

export const capUsed = (fee: CaseFee): number | null =>
  fee.arrangement === "hourly_capped" && fee.amount
    ? Math.min(100, Math.round((fee.spent / fee.amount) * 100))
    : null;
